import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import PageHeader from "../components/PageHeader";
import { Loader2, ShieldCheck, Users, Briefcase, Copy, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-[#0b0811] border border-white/10 text-white rounded px-2 py-1.5 text-xs";

// Tri-état : undefined = hérité, true = forcé oui, false = forcé non
function Cell({ base, override, onChange, testId }) {
  const forced = override !== undefined && override !== null;
  const effective = forced ? override : !!base;
  const next = () => onChange(!forced ? !base : override === true ? false : null);
  const title = forced ? (override ? "Forcé : autorisé (clic → interdit)" : "Forcé : interdit (clic → hérité)") : `Hérité : ${base ? "autorisé" : "interdit"} (clic → forcer)`;
  return (
    <button onClick={next} title={title} data-testid={testId}
      className={`w-7 h-7 rounded-md text-[10px] font-black transition-colors ${effective ? "bg-emerald-500/25 text-emerald-300" : "bg-white/5 text-slate-600"} ${forced ? "ring-2 ring-[#f7c935]" : ""}`}>
      {effective ? "✓" : "–"}
    </button>
  );
}

export default function SocietePermissionsPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const { currentSociete, societes, switchSociete } = useSociete();
  const societeId = currentSociete?.societe_id || societes?.[0]?.societe_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState("postes");
  const [module, setModule] = useState("");
  const [copyTo, setCopyTo] = useState("");

  const load = useCallback(async () => {
    if (!societeId) return;
    setLoading(true);
    try { const r = await api.get("/societe-permissions/matrix", { params: { societe_id: societeId } }); setData(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Chargement impossible"); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societeId]);
  useEffect(() => { load(); }, [load]);

  const modules = useMemo(() => {
    const m = {};
    (data?.catalog || []).forEach((c) => { m[c.module] = c.module_label; });
    return Object.entries(m);
  }, [data]);
  const cols = useMemo(() => (data?.catalog || []).filter((c) => !module || c.module === module), [data, module]);
  const rows = scope === "postes" ? (data?.postes || []).map((p) => ({ id: p.poste_id, label: p.name, sub: "poste", base: p.base, overrides: p.overrides, scope: "poste" }))
    : (data?.members || []).map((m) => ({ id: m.user_id, label: m.name, sub: m.email, base: m.base, overrides: m.overrides, scope: "user" }));

  const setOverride = async (row, key, value) => {
    try {
      const r = await api.put("/societe-permissions/override", { societe_id: societeId, scope: row.scope, ref_id: row.id, key, value });
      setData((d) => {
        const list = row.scope === "poste" ? "postes" : "members";
        const idKey = row.scope === "poste" ? "poste_id" : "user_id";
        return { ...d, [list]: d[list].map((x) => (x[idKey] === row.id ? { ...x, overrides: r.data.overrides } : x)) };
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const resetRow = async (row) => {
    if (!window.confirm(`Supprimer toutes les surcharges de « ${row.label} » pour cette société ?`)) return;
    for (const k of Object.keys(row.overrides || {})) await setOverride(row, k, null);
    toast.success("Surcharges supprimées");
    load();
  };
  const copy = async () => {
    if (!copyTo) return;
    try { const r = await api.post("/societe-permissions/copy", { from_societe_id: societeId, to_societe_id: copyTo }); toast.success(`${r.data.copied} surcharge(s) copiée(s)`); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="societe-permissions-page">
      <PageHeader title="Autorisations par société" description="Ajustez, société par société, les droits de chaque poste et de chaque membre. Vert = autorisé, contour jaune = surcharge propre à cette société." icon={<ShieldCheck />} testId="societe-permissions-header" />
      <div className="flex flex-wrap items-center gap-2">
        <select value={societeId || ""} onChange={(e) => { const s = societes.find((x) => x.societe_id === e.target.value); if (s) switchSociete(s); }} className={inputCls} data-testid="perm-societe-selector">
          {(societes || []).map((s) => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
        </select>
        <div className="flex rounded-lg overflow-hidden border border-white/10">
          <button onClick={() => setScope("postes")} className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${scope === "postes" ? "bg-[#f7c935] text-[#0b0811]" : "text-slate-400"}`} data-testid="perm-scope-postes"><Briefcase className="w-3.5 h-3.5" /> Par poste</button>
          <button onClick={() => setScope("members")} className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${scope === "members" ? "bg-[#f7c935] text-[#0b0811]" : "text-slate-400"}`} data-testid="perm-scope-members"><Users className="w-3.5 h-3.5" /> Par membre</button>
        </div>
        <select value={module} onChange={(e) => setModule(e.target.value)} className={inputCls} data-testid="perm-module-filter">
          <option value="">Tous les modules</option>
          {modules.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-1">
          <select value={copyTo} onChange={(e) => setCopyTo(e.target.value)} className={inputCls} data-testid="perm-copy-target">
            <option value="">Copier vers…</option>
            {(societes || []).filter((s) => s.societe_id !== societeId).map((s) => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
          </select>
          <button onClick={copy} disabled={!copyTo} className="px-2 py-1.5 rounded bg-white/5 text-xs text-slate-200 hover:bg-white/10 disabled:opacity-40 flex items-center gap-1" data-testid="perm-copy-btn"><Copy className="w-3.5 h-3.5" /> Copier</button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div> : (
        <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-x-auto" data-testid="perm-matrix">
          <table className="text-xs">
            <thead>
              <tr className="border-b border-white/10">
                <th className="sticky left-0 bg-[#14101c] text-left px-3 py-2 text-slate-400 font-bold min-w-[200px]">{scope === "postes" ? "Poste" : "Membre"}</th>
                {cols.map((c) => <th key={c.key} className="px-1 py-2 text-slate-500 font-medium" title={`${c.module_label} — ${c.key}`}><div className="w-7 [writing-mode:vertical-rl] rotate-180 whitespace-nowrap text-[10px] mx-auto">{c.label}</div></th>)}
                <th className="px-2" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td className="px-3 py-6 text-slate-500" colSpan={cols.length + 2}>Aucun {scope === "postes" ? "poste" : "membre"}.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`perm-row-${r.id}`}>
                  <td className="sticky left-0 bg-[#14101c] px-3 py-1.5"><div className="text-white font-medium">{r.label}</div><div className="text-[10px] text-slate-500">{r.sub}{Object.keys(r.overrides || {}).length ? ` · ${Object.keys(r.overrides).length} surcharge(s)` : ""}</div></td>
                  {cols.map((c) => <td key={c.key} className="px-1 py-1 text-center"><Cell base={r.base?.[c.key]} override={r.overrides?.[c.key]} onChange={(v) => setOverride(r, c.key, v)} testId={`perm-cell-${r.id}-${c.key}`} /></td>)}
                  <td className="px-2"><button onClick={() => resetRow(r)} title="Supprimer les surcharges" className="p-1 text-slate-500 hover:text-white" data-testid={`perm-reset-${r.id}`}><RotateCcw className="w-3.5 h-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[11px] text-slate-500">Un clic sur une case : hérité → forcé (inverse) → forcé interdit → hérité. Les surcharges s'appliquent quand la société est sélectionnée dans le bandeau ; le poste s'applique avant le membre.</div>
    </div>
  );
}
